import {type ComponentProps, useEffect, useState} from "react"
import {useNavigate} from "react-router-dom"
import {format} from "date-fns"
import {Printer, SlidersHorizontal, TriangleAlert} from "lucide-react"
import type {OtkDocumentDetail, OtkDocumentResult, OtkPrintProfile, SignedOtkDocumentPayload} from "shared"

import {Alert, AlertDescription, AlertTitle} from "@/components/ui/alert"
import {Badge} from "@/components/ui/badge"
import {Button} from "@/components/ui/button"
import {Card, CardContent, CardDescription, CardHeader, CardTitle,} from "@/components/ui/card"
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,} from "@/components/ui/dialog"
import {RadioGroup, RadioGroupItem} from "@/components/ui/radio-group"
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue,} from "@/components/ui/select"
import {Tabs, TabsList, TabsTrigger} from "@/components/ui/tabs"
import {DocumentPreviewCanvas} from "@/components/DocumentPreviewCanvas"
import {DocumentCard, type DocumentPreviewSection,} from "@/components/document-editor/DocumentCard"
import {DocumentDetailField} from "@/components/DocumentDetailField"
import {SignDialogForm, type SignedDocumentInfo} from "@/components/document-editor/SignDialog"
import {SignatureDetailsDialog} from "@/components/SignatureDetailsDialog"
import {submitSignedOtkDocument} from "@/lib/api/documents"
import {documentRetryErrorMessage, documentSignErrorMessage, pendingDocumentFromError} from "@/lib/documentEditor"
import {loadPrintProfiles} from "@/lib/api/printProfiles"
import {cn} from "@/lib/utils"
import {useReadOnly, useRolePermissions} from "@/store/session"

type PreviewTab = "electronic" | "protocol" | "printed"

export interface DocumentPreviewPrintData {
  documentNumber: string
  vehicle: string
  result: OtkDocumentResult
  signed?: boolean
  /** Live editor data mapped to every protocol v2 field (`documentPreviewFields.ts`'s `protocolV2PrintedFields`) — feeds the "printed" tab's `DocumentPreviewCanvas`. */
  fieldValues: Record<string, string>
}

/**
 * document-editor.md's DE-preview, merged this pass: `DocumentPreviewDialog`
 * now owns preview, print, *and* signing together (the former standalone
 * `SignDialog`/DE10 trigger was deleted — see `SignDialog.tsx`'s own note).
 * Three real, backend-shaped things live inside it: the "Перегляд Введених
 * Даних" tab (`DocumentCard` over `sections`, same component `DocumentModal`
 * view mode already uses), the "Перегляд Друку на Бланку" tab
 * (`DocumentPreviewCanvas` over a real saved print profile, loaded via
 * `loadPrintProfiles` once print profiles existed — `lib/api/printProfiles.ts`),
 * and the merged `SignDialogForm` sign panel. "Перегляд Електронного
 * Документа" remains an honest placeholder — it needs a rendered
 * electronic-document pipeline this app doesn't have yet, unrelated to print
 * profiles (document-editor.md's own note: "remains a placeholder until the
 * electronic document renderer exists").
 */
export function DocumentPreviewDialog({
  printData,
  sections,
  triggerLabel = "Перегляд та Друк",
  triggerVariant = "outline",
  punkt,
  isNonconformityAct = false,
  disabled = false,
  disabledReasons = [],
  restricted = false,
  submissionPending = false,
  submissionConfirmed = false,
  retryAt,
  onRetry,
  documentId,
  buildSignPayload,
  onSigned,
}: {
  printData: DocumentPreviewPrintData
  sections?: DocumentPreviewSection[]
  triggerLabel?: string
  triggerVariant?: ComponentProps<typeof Button>["variant"]
  /** When provided, renders the signing panel to the left of the tabs. */
  punkt?: string
  isNonconformityAct?: boolean
  disabled?: boolean
  disabledReasons?: string[]
  restricted?: boolean
  /** The document is signed and stored as `SUBMISSION_PENDING` — ЄДРТЗ hasn't accepted it yet. */
  submissionPending?: boolean
  submissionConfirmed?: boolean
  /** Epoch ms of the document's `nextRetryAt` — the earliest moment `POST /otk/documents/:id/retry` accepts. */
  retryAt?: number
  onRetry?: () => Promise<void>
  /** Draft id `POST /otk/documents/:id/sign` targets — required together with `punkt`/`buildSignPayload` for the signing panel to actually submit. */
  documentId?: string
  /** Builds the exact JSON body to sign for the chosen electronic/blank units (`SignDialogForm`'s own `buildPayload`). */
  buildSignPayload?: (selection: {
    electronicSeries: string
    electronicNumber: string
    blankSeries?: string
    blankNumber?: string
  }) => Promise<SignedOtkDocumentPayload>
  /** Reports the updated document (new `status`/`version`/`signedAt`) up to whichever route owns `detail`, once signing succeeds. */
  onSigned?: (document: OtkDocumentDetail) => void
}) {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<PreviewTab>("electronic")
  const [needsBlank, setNeedsBlank] = useState(false)
  const [signedInfo, setSignedInfo] = useState<
    (SignedDocumentInfo & { signedAt: Date | null }) | null
  >(null)
  const [retrying, setRetrying] = useState(false)
  const [retryError, setRetryError] = useState<string | null>(null)
  const [now, setNow] = useState(0)
  const readOnly = useReadOnly()
  const { signAndSendEdrtz } = useRolePermissions()

  // "printed" tab data — loaded once per dialog open, not per mount, so a
  // profile saved on `/print-profiles` while this dialog was last open is
  // picked up the next time it opens rather than staying stale for the rest
  // of the session.
  const [printProfiles, setPrintProfiles] = useState<OtkPrintProfile[]>([])
  const [selectedProfileId, setSelectedProfileId] = useState("")
  useEffect(() => {
    if (!open) return
    let cancelled = false
    loadPrintProfiles()
      .then((profiles) => {
        if (cancelled) return
        setPrintProfiles(profiles)
        setSelectedProfileId((current) =>
          profiles.some((profile) => profile.id === current)
            ? current
            : (profiles[0]?.id ?? "")
        )
      })
      .catch((error) => {
        console.error("Не вдалося завантажити профілі друку", error)
      })
    return () => {
      cancelled = true
    }
  }, [open])
  const selectedPrintProfile = printProfiles.find(
    (profile) => profile.id === selectedProfileId
  )

  const signDisabled = restricted || readOnly || disabled
  const signDisabledReasons = [
    ...disabledReasons,
    ...(restricted ? ["Очікується оплата за доступ до АРМ."] : []),
    ...(readOnly
      ? ["Суб'єкта ОТК виключено з реєстру; доступний лише перегляд."]
      : []),
  ]
  const documentStatus = Boolean(printData.signed) || signedInfo !== null
  const awaitingRegistry = !submissionConfirmed && (submissionPending || signedInfo !== null)
  useEffect(() => {
    if (!awaitingRegistry) return
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [awaitingRegistry])

  async function handleRetry() {
    if (!onRetry) return
    setRetrying(true)
    setRetryError(null)
    try {
      await onRetry()
    } catch (error) {
      setRetryError(documentRetryErrorMessage(error))
    } finally {
      setRetrying(false)
    }
  }
  const showPrintedTab = punkt === undefined || needsBlank

  function handleNeedsBlankChange(value: boolean) {
    setNeedsBlank(value)
    if (!value && activeTab === "printed") setActiveTab("electronic")
  }

  // `SignDialogForm`'s own `onSubmit` — sends the exact signed JSON bytes
  // (`submitSignedOtkDocument`, `POST /otk/documents/:id/sign`) and maps a
  // failed call to a Ukrainian message, same pattern as
  // `destructionActSubmitErrorMessage`/`DestructionActDialog`'s `onFinalize`.
  async function handleSignSubmit(
    signedPayload: string,
    kepSignature: string
  ): Promise<
    { ok: true; document: OtkDocumentDetail } | { ok: false; message: string }
  > {
    if (!documentId) {
      return { ok: false, message: "Документ не готовий до підписання." }
    }
    try {
      const document = await submitSignedOtkDocument(
        documentId,
        signedPayload,
        kepSignature
      )
      onSigned?.(document)
      return { ok: true, document }
    } catch (error) {
      // Signed and stored, but the first ЄДРТЗ attempt failed — report the
      // persisted document so the panel switches to its retry state.
      const pending = pendingDocumentFromError(error)
      if (pending) {
        onSigned?.(pending)
        return { ok: true, document: pending }
      }
      return { ok: false, message: documentSignErrorMessage(error) }
    }
  }

  return (
    <Dialog
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen)
        if (nextOpen) {
          setActiveTab("electronic")
          setNeedsBlank(false)
          setSignedInfo(null)
        }
      }}
      open={open}
    >
      <DialogTrigger asChild>
        <Button
          className="w-full min-w-0 justify-center truncate lg:h-9 lg:px-2.5 lg:text-sm"
          variant={triggerVariant}
        >
          <Printer data-icon="inline-start" />
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-none grid-rows-[auto_minmax(0,1fr)] overflow-hidden p-6 sm:max-w-none">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Підпис та Друк Документа {printData.documentNumber}
            <Badge variant={awaitingRegistry ? "warning" : documentStatus ? "success" : "warning"}>
              {awaitingRegistry ? "Очікує підтвердження ЄДРТЗ" : documentStatus ? "Документ ПІДПИСАНО" : "Документ НЕ ПІДПИСАНО"}
            </Badge>
          </DialogTitle>
        </DialogHeader>
        <div className="flex min-h-0 gap-6">
          {punkt !== undefined && (
            <div className="flex w-[300px] min-w-0 shrink-0 flex-col gap-4 overflow-y-auto">
              {!isNonconformityAct && (
                <Card className="shrink-0 bg-muted/30 shadow-none">
                  <CardHeader>
                    <CardTitle>Необхідні Документи:</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <RadioGroup
                      disabled={documentStatus}
                      onValueChange={(value) =>
                        handleNeedsBlankChange(value === "print")
                      }
                      value={needsBlank ? "print" : "electronic"}
                    >
                      <div className="flex items-center gap-2">
                        <RadioGroupItem
                          id="preview-needs-blank-electronic"
                          value="electronic"
                        />
                        <label
                          className="text-sm"
                          htmlFor="preview-needs-blank-electronic"
                        >
                          Тільки Електронний протокол
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem
                          id="preview-needs-blank-print"
                          value="print"
                        />
                        <label
                          className="text-sm"
                          htmlFor="preview-needs-blank-print"
                        >
                          Електронний протокол + Друк на бланку суворої звітності
                        </label>
                      </div>
                    </RadioGroup>
                  </CardContent>
                </Card>
              )}
              {awaitingRegistry ? (
                <Card className="min-h-0 flex-1 overflow-y-auto bg-muted/30 shadow-none">
                  <CardHeader>
                    <CardTitle>Документ підписано</CardTitle>
                    <CardDescription>ЄДРТЗ ще не підтвердив прийняття документа. Повторне надсилання відправляє той самий підписаний документ — новий підпис не потрібен.</CardDescription>
                  </CardHeader>
                  {signedInfo && <CardContent className="space-y-3">
                    <dl className="flex flex-col gap-3">
                      {signedInfo.signedAt && <DocumentDetailField
                        label="Дата та час підписання:"
                        value={format(signedInfo.signedAt, "dd.MM.yyyy HH:mm")}
                      />}
                      <DocumentDetailField label="Електронний документ ОТК:" value={signedInfo.electronicLabel} />
                      {signedInfo.blankLabel && <DocumentDetailField label="Бланк суворої звітності:" value={signedInfo.blankLabel} />}
                      <DocumentDetailField label="Носій особистого ключа:" value={signedInfo.keyLabel} />
                    </dl>
                    {/* document-editor.md: after the `dl`, the full-width «Деталі Підпису» button (01.10.2026). */}
                    <SignatureDetailsDialog signedAt={signedInfo.signedAt} />
                  </CardContent>}
                  {onRetry && !readOnly && signAndSendEdrtz && <CardContent className="space-y-2">
                    <Button disabled={retrying || retryAt === undefined || now < retryAt} onClick={() => void handleRetry()}>
                      {retrying ? "Надсилання..." : "Повторити надсилання"}
                    </Button>
                    {retryAt !== undefined && now < retryAt && <p className="text-sm text-muted-foreground">
                      Повторна спроба доступна о {format(new Date(retryAt), "HH:mm:ss")}.
                    </p>}
                    {retryError && <p className="text-sm text-destructive">{retryError}</p>}
                  </CardContent>}
                </Card>
              ) : documentStatus ? (
                <Card className="min-h-0 flex-1 bg-muted/30 shadow-none">
                  <CardHeader>
                    <CardTitle>Документ підтверджено ЄДРТЗ</CardTitle>
                  </CardHeader>
                </Card>
              ) : signDisabled ? (
                <Alert variant="warning">
                  <TriangleAlert aria-hidden />
                  <AlertTitle>
                    Підписання документа наразі недоступне через:
                  </AlertTitle>
                  <AlertDescription>
                    <ul className="list-disc pl-4">
                      {signDisabledReasons.map((reason) => (
                        <li key={reason}>{reason}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              ) : (
                <Card className="min-h-0 flex-1 overflow-y-auto bg-muted/30 shadow-none">
                  <CardHeader>
                    <CardTitle>Підписання документа</CardTitle>
                    <CardDescription>
                      Перед підписанням перевіряються поля, фото та вибір
                      спецпродукції.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex flex-col gap-4">
                    {open && documentId && buildSignPayload ? (
                      <SignDialogForm
                        buildPayload={buildSignPayload}
                        documentNumber={printData.documentNumber}
                        isNonconformityAct={isNonconformityAct}
                        needsBlank={needsBlank}
                        onSigned={(document, info) =>
                          setSignedInfo({
                            ...info,
                            // `pidpysannya-dokumenta-otk.md`: "мітка часу
                            // ... ніколи не походить із годинника клієнта" —
                            // the server's own `signedAt` from the sign
                            // response, not `new Date()`.
                            signedAt: document.signedAt
                              ? new Date(document.signedAt)
                              : null,
                          })
                        }
                        onSubmit={handleSignSubmit}
                      />
                    ) : (
                      open && (
                        <p className="text-sm text-muted-foreground">
                          Підписання наразі недоступне: документ ще не
                          завантажено.
                        </p>
                      )
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
          <div className="flex min-h-0 flex-1 flex-col gap-3">
            <Tabs
              onValueChange={(value) => setActiveTab(value as PreviewTab)}
              value={activeTab}
            >
              <TabsList className="!h-10 w-full justify-start">
                <TabsTrigger className="px-4 text-sm" value="electronic">
                  Перегляд Введених Даних
                </TabsTrigger>
                <TabsTrigger className="px-4 text-sm" value="protocol">
                  Перегляд Електронного Документа
                </TabsTrigger>
                {showPrintedTab && (
                  <TabsTrigger className="px-4 text-sm" value="printed">
                    Перегляд Друку на Бланку
                  </TabsTrigger>
                )}
              </TabsList>
            </Tabs>
            <div className="flex flex-wrap items-center gap-4 pb-0">
              {activeTab === "printed" && (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium whitespace-nowrap">
                    Профіль друку:
                  </span>
                  <Select
                    disabled={printProfiles.length === 0}
                    onValueChange={setSelectedProfileId}
                    value={selectedProfileId}
                  >
                    <SelectTrigger aria-label="Профіль друку" className="w-56">
                      <SelectValue placeholder="Немає профілів" />
                    </SelectTrigger>
                    <SelectContent>
                      {printProfiles.map((profile) => (
                        <SelectItem key={profile.id} value={profile.id}>
                          {profile.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    aria-label="Профілі друку та калібрування"
                    onClick={() => {
                      setOpen(false)
                      navigate("/print-profiles")
                    }}
                    size="icon"
                    title="Профілі друку та калібрування"
                    variant="ghost"
                  >
                    <SlidersHorizontal />
                  </Button>
                </div>
              )}
              <div className="ml-auto flex gap-2">
                <Button className="w-40" variant="outline">
                  <Printer data-icon="inline-start" />
                  Зберегти як PDF
                </Button>
                <Button className="w-40">
                  <Printer data-icon="inline-start" />
                  Друк
                </Button>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-hidden">
              {activeTab === "printed" &&
              selectedPrintProfile &&
              selectedPrintProfile.blankVersion === 2 ? (
                <div className="h-full w-full overflow-hidden rounded-lg border border-neutral-600 bg-neutral-700">
                  <DocumentPreviewCanvas
                    calibration={false}
                    className="h-full"
                    elementScale={selectedPrintProfile.fieldScale}
                    exampleValues={printData.fieldValues}
                    fieldAutoSizeSource="examples"
                    fields={selectedPrintProfile.fieldLayouts}
                    initialFieldContentMode="examples"
                    offsets={selectedPrintProfile.fieldOffsets}
                    scanSrc="/assets/blank-protocol-v2-front.jpg"
                    showBlankControl={false}
                    showFieldBlocks
                    showFieldContentControls={false}
                    showFieldList={false}
                    showMockContent={false}
                  />
                </div>
              ) : (
                <div
                  className={cn(
                    "flex h-full min-h-0 flex-col items-center justify-center rounded-lg bg-neutral-700 text-center text-lg font-medium text-white",
                    (activeTab === "printed" || activeTab === "protocol") &&
                      "border border-neutral-600"
                  )}
                >
                  {activeTab === "printed" &&
                    (selectedPrintProfile?.blankVersion === 1 ? (
                      <p>Скан бланка версії №1 ще не додано до репозиторію.</p>
                    ) : printProfiles.length === 0 ? (
                      <p>
                        Немає налаштованих профілів друку — створіть профіль
                        на сторінці «Профілі друку та калібрування».
                      </p>
                    ) : (
                      <p>Завантаження профілю друку...</p>
                    ))}
                  {activeTab === "protocol" && <p>Електронний Документ</p>}
                  {activeTab === "electronic" &&
                    (sections && sections.length > 0 ? (
                      <div className="h-full w-full overflow-y-auto bg-background text-foreground">
                        <DocumentCard className="pt-0 sm:pt-0" sections={sections} />
                      </div>
                    ) : (
                      <p>ТУТ БУДЕ ЕЛЕКТРОННИЙ ДОКУМЕНТ</p>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
